import React, { useState, useEffect } from 'react'
import {
  Autocomplete,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  CircularProgress,
  Typography,
  TextField as MuiTextField,
  Alert,
} from '@mui/material'
import { useRedirect, useNotify, useGetList } from 'react-admin'
import { getJson, postJson, getUrl } from '../../providers/data'
import {
  StadsprognoseFormData,
  defaultFormData,
} from '../../types/stadsprognoseTypes'
import { GemeenteResponse, StadsprognoseConfig } from '../../types/apiRequests'
import Step1Basis from './steps/Step1Basis'
import Step2Stroomgegevens from './steps/Step2Stroomgegevens'
import Step3Woningbouwplanning from './steps/Step3Woningbouwplanning'
import Step4BepalenStuurkaart from './steps/Step4BepalenStuurkaart'
import Step5Stuurkaart from './steps/Step5Stuurkaart'

const steps = [
  'Basis',
  'Stroomgegevens',
  'Woningbouwplanning',
  'Bepalen Stuurkaart',
  'Stuurkaart',
]

const StadsprognoseCreate: React.FC = () => {
  const redirect = useRedirect()
  const notify = useNotify()

  const [gemeenteSearch, setGemeenteSearch] = useState('')
  const { data: gemeenten = [], isLoading: gemeentenLoading } = useGetList<GemeenteResponse>('gemeenten', {
    sort: { field: 'naam', order: 'ASC' },
    pagination: { page: 1, perPage: 25 },
    filter: gemeenteSearch ? { q: gemeenteSearch } : {},
  })
  const [selectedGemeente, setSelectedGemeente] = useState<GemeenteResponse | null>(null)

  const [activeStep, setActiveStep] = useState(0)
  const [formData, setFormData] = useState<StadsprognoseFormData>(defaultFormData)
  const [config, setConfig] = useState<StadsprognoseConfig | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load config when gemeente changes
  useEffect(() => {
    if (!formData.gemeente_code) return

    const fetchConfig = async () => {
      setLoading(true)
      try {
        const result = await getJson<StadsprognoseConfig>(
          `${getUrl()}/gemeenten/${formData.gemeente_code}/stadsprognose/config`
        )
        setConfig(result)
        if (result.max_bjaar) {
          setFormData((prev) => ({
            ...prev,
            bjaar: result.max_bjaar ?? prev.bjaar,
          }))
        }
      } catch (err) {
        console.error('Failed to load config:', err)
        setError(`Kan configuratie niet laden: ${String(err)}`)
      } finally {
        setLoading(false)
      }
    }
    void fetchConfig()
  }, [formData.gemeente_code])

  const handleChange = (updates: Partial<StadsprognoseFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }))
  }

  const handleNext = () => setActiveStep((prev) => prev + 1)
  const handleBack = () => setActiveStep((prev) => prev - 1)

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const result = await postJson<{ id: string; status: string }>(
        `${getUrl()}/gemeenten/${formData.gemeente_code}/stadsprognose`,
        {
          naam: formData.naam,
          omschrijving: formData.omschrijving,
          bjaar: formData.bjaar,
          ejaar: formData.ejaar,
          bron: formData.bron,
          calc_method_salit: formData.calc_method_salit,
          calc_method_stk: formData.calc_method_stk,
          aanname_tfrn: formData.aanname_tfrn,
          aanname_geb: formData.aanname_geb,
          aanname_ste: formData.aanname_ste,
          aanname_ver: formData.aanname_ver,
          aanname_ves: formData.aanname_ves,
          correctie_sexratio: formData.correctie_sexratio,
          correctie_stk: formData.correctie_stk,
          correctie_tfrn: formData.correctie_tfrn,
          correctie_salut: formData.correctie_salut,
          correctie_fbewwon: formData.correctie_fbewwon,
          afronden: formData.afronden,
          selected_wbp_code: formData.selected_wbp_code,
          selected_ext_code: formData.selected_ext_code,
          selected_matrix_code: formData.selected_matrix_code,
          stuurkaart_data: formData.stuurkaart_data,
        }
      )
      notify('Prognose berekening gestart', { type: 'info' })
      redirect('show', 'prognose_sets', result.id)
    } catch (err) {
      setError(`Fout bij starten berekening: ${String(err)}`)
      notify('Fout bij starten berekening', { type: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  const isStepValid = (): boolean => {
    switch (activeStep) {
      case 0:
        return !!formData.naam && !!formData.gemeente_code && formData.bjaar > 0 && formData.ejaar > formData.bjaar
      case 1:
        return !!formData.aanname_geb && !!formData.aanname_ste && !!formData.aanname_ver && !!formData.aanname_ves
      case 2:
        return !!formData.selected_wbp_code
      case 3:
        return !!formData.correctie_tfrn
      case 4:
        return !!formData.stuurkaart_data && formData.stuurkaart_data.length > 0
      default:
        return false
    }
  }

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <Step1Basis formData={formData} onChange={handleChange} config={config} />
      case 1:
        return <Step2Stroomgegevens formData={formData} onChange={handleChange} config={config} />
      case 2:
        return <Step3Woningbouwplanning formData={formData} onChange={handleChange} config={config} />
      case 3:
        return <Step4BepalenStuurkaart formData={formData} onChange={handleChange} config={config} />
      case 4:
        return <Step5Stuurkaart formData={formData} onChange={handleChange} />
      default:
        return null
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>Nieuwe Stadsprognose</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Autocomplete
          options={gemeenten}
          getOptionLabel={(option) => `${option.naam} (${option.gm_code})`}
          loading={gemeentenLoading}
          value={selectedGemeente}
          onInputChange={(_e, value) => setGemeenteSearch(value)}
          onChange={(_e, value) => {
            setSelectedGemeente(value)
            handleChange({ gemeente_code: value?.gm_code || '' })
          }}
          isOptionEqualToValue={(option, value) => option.gm_code === value.gm_code}
          renderInput={(params) => (
            <MuiTextField {...params} label="Gemeente" fullWidth />
          )}
        />
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="body2">Configuratie laden...</Typography>
          </Box>
        )}
      </Paper>

      {formData.gemeente_code && config && (
        <>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Paper sx={{ p: 3, mb: 2 }}>
            {renderStep()}
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Terug
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => void handleSubmit()}
                  disabled={submitting || !isStepValid()}
                  startIcon={submitting ? <CircularProgress size={20} /> : undefined}
                >
                  Prognose genereren
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!isStepValid()}
                >
                  Volgende stap
                </Button>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}

export default StadsprognoseCreate
